/**
 * RegionalGuidesIndex — hub page listing every programmatic-SEO regional guide.
 *
 * URL: /wine-country-guides
 *
 * Purpose:
 *   * Give Google + AI crawlers a single high-authority index page linking
 *     to every /wineries/:region/best-:intent-itineraries page. Boosts
 *     PageRank flow into the long-tail landing pages.
 *   * Give humans a browsable directory of the curated collections.
 *
 * Data: GET /api/regional-guides + GET /api/guides (database, published
 * only — the same tables the sitemap reads), falling back to the shipped
 * JSON files when the API is unreachable (lib/guidesSource).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import useJsonLd from '@/hooks/useJsonLd';
import InArticleAd from '@/components/ads/InArticleAd';
import { loadRegionalGuides, loadTopicalGuides } from '@/lib/guidesSource';

const SITE_URL = 'https://www.wineryfinderapp.com';

const RegionalGuidesIndex = () => {
  const [guides, setGuides] = useState([]);
  const [topicalGuides, setTopicalGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  // 'fallback' if either list came from the shipped JSON (data-guide-source)
  const [source, setSource] = useState(null);

  useEffect(() => {
    (async () => {
      const sources = [];
      const onSource = (s) => sources.push(s);
      try {
        const [regional, topical] = await Promise.all([
          loadRegionalGuides({ onSource }), loadTopicalGuides({ onSource }),
        ]);
        setGuides(regional);
        setTopicalGuides(topical);
      } catch { /* graceful degradation */ }
      finally {
        setSource(sources.length ? (sources.includes('fallback') ? 'fallback' : 'api') : null);
        setLoading(false);
      }
    })();
  }, []);

  // Group by region for the index layout.
  const groups = useMemo(() => {
    const byRegion = {};
    for (const g of guides) {
      const key = g.region || 'Other';
      if (!byRegion[key]) byRegion[key] = [];
      byRegion[key].push(g);
    }
    return Object.entries(byRegion).sort(([a], [b]) => a.localeCompare(b));
  }, [guides]);

  const title = 'Wine Country Guides — Best Itineraries by Region & Style';
  const description =
    'Browse every curated wine-country trip collection — from dog-friendly Napa to Pinot Noir in Willamette Valley. Each guide features 12 community-voted, AI-planned 3-stop itineraries.';

  // CollectionPage JSON-LD helps Google and AI engines understand this is
  // a curated hub linking to the individual guides.
  const collectionJson = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: `${SITE_URL}/wine-country-guides`,
    hasPart: guides.map((g) => ({
      '@type': 'WebPage',
      name: `Best ${g.intent} Itineraries in ${g.region}`,
      url: `${SITE_URL}/wineries/${g.regionSlug}/best-${g.intentSlug}-itineraries`,
    })),
  };

  // Inject CollectionPage schema directly into <head> (react-helmet-async
  // does not reliably lift inline <script> children in React 18).
  useJsonLd('collection', guides.length > 0 ? collectionJson : null);

  return (
    <div className="max-w-5xl mx-auto py-16 md:py-20 px-6" data-testid="regional-guides-index" data-guide-source={source || undefined}>
      <Helmet>
        <title>{`${title} | Winery Finder`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SITE_URL}/wine-country-guides`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Helmet>

      <motion.header
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
      >
        <div className="text-xs font-bold uppercase tracking-widest text-wine-600">
          Curated collections
        </div>
        <h1 className="mt-3 text-4xl md:text-5xl font-serif tracking-tight text-gray-900">
          Wine Country Guides
        </h1>
        <p className="mt-6 text-lg text-gray-700 max-w-3xl leading-relaxed">
          Every collection below is a hand-selected slice of wine country: a specific
          region paired with a specific intent (dog-friendly, romantic, budget, weekend, etc.).
          Each page hosts 12 community-voted 3-stop day trips ready to book.
        </p>
      </motion.header>

      {loading && (
        <div className="mt-20 text-sm text-gray-500" data-testid="regional-guides-loading">
          Loading guides…
        </div>
      )}

      {!loading && guides.length === 0 && (
        <div
          className="mt-20 text-sm text-gray-500 border rounded-xl p-6 bg-gray-50"
          data-testid="regional-guides-empty"
        >
          Guides coming soon.
        </div>
      )}

      {/* Groups are spaced with mt-14 rather than space-y-14 so the
          (zero-height until filled) ad between groups 1 and 2 adds no gap. */}
      <div className="mt-16">
        {groups.map(([region, items], gi) => (
          <Fragment key={region}>
            {gi === 1 && <InArticleAd position="guides-index" gap="3.5rem" />}
            <section className={gi > 0 ? 'mt-14' : undefined} data-testid={`region-group-${items[0]?.regionSlug}`}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 pb-3 border-b border-gray-200">
                {region}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((g) => (
                  <Link
                    key={`${g.regionSlug}/${g.intentSlug}`}
                    to={`/wineries/${g.regionSlug}/best-${g.intentSlug}-itineraries`}
                    className="block p-6 rounded-xl border border-gray-200 hover:border-wine-400 hover:shadow-md transition"
                    data-testid={`regional-guide-card-${g.regionSlug}-${g.intentSlug}`}
                  >
                    <div className="text-xs uppercase tracking-widest text-wine-600 mb-2">
                      {g.intent}
                    </div>
                    <div className="font-semibold text-lg text-gray-900 mb-2">
                      Best {g.intent} Itineraries in {g.region}
                    </div>
                    <div className="text-sm text-gray-600 line-clamp-3">
                      {g.heroIntro}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </Fragment>
        ))}
      </div>

      {topicalGuides.length > 0 && (
        <section className="mt-20 pt-12 border-t border-gray-200" data-testid="topical-guides-section">
          <div className="mb-8">
            <div className="text-xs font-bold uppercase tracking-widest text-wine-600 mb-2">
              Topical guides
            </div>
            <h2 className="text-2xl md:text-3xl font-serif tracking-tight text-gray-900">
              Deep-dives, logistics & seasonal picks
            </h2>
            <p className="mt-3 text-base text-gray-600 max-w-2xl">
              Long-form editorial guides that go beyond itineraries — single-winery
              deep-dives, transportation, seasonal attire, and holiday planning.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topicalGuides.map((t) => (
              <Link
                key={t.slug}
                to={`/guides/${t.slug}`}
                className="block p-5 rounded-xl border border-gray-200 hover:border-wine-400 hover:shadow-md transition"
                data-testid={`topical-guide-card-${t.slug.replace('/', '-')}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-widest text-wine-600 font-semibold">
                    {t.category}
                  </span>
                  {t.readTime && (
                    <span className="text-xs text-gray-500">{t.readTime}</span>
                  )}
                </div>
                <div className="font-semibold text-gray-900 mb-2 leading-snug">
                  {t.title}
                </div>
                <div className="text-sm text-gray-600 line-clamp-3">
                  {t.tldr}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-24 pt-10 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Can&apos;t find your combination?{' '}
          <Link to="/concierge" className="text-wine-600 underline">
            Use the AI concierge
          </Link>{' '}
          — it plans a personalized 3-stop day in seconds.
        </p>
      </section>
    </div>
  );
};

export default RegionalGuidesIndex;
